import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, Toast, ToastVariant } from '@hungryroot/components-toast';
import {
  StatusCode,
  getFavoriteProductsPath,
  postFavoriteProducts,
  type CustomerFavoriteProducts,
} from '@hungryroot/rest';
import { getFavoritesCookbookQueryKey } from '@hungryroot/rest-hooks';
import {
  type AccessedFrom,
  type FeedInteractionFields,
  type Location,
} from '@hungryroot/segment';
import { Warning } from '@hungryroot/ui-kit';
import { trackFavorite } from '../segment/track-favorite';

/**
 * Spreads {@link FeedInteractionFields} so a call site inside a marketing
 * carousel can supply `container` / `carousel_id` / `carousel_name` /
 * `horizontal_position` per the Exp 242 nav-IA logging spec.
 */
type FavoriteProductSegment = FeedInteractionFields & {
  location: Location | null;
  orderId?: number;
  pageName?: string;
  accessedFrom?: AccessedFrom;
  conversationId?: string;
  searchId?: string;
  resultPosition?: number;
};

interface FavoriteProductData {
  customerId: number;
  productId: number;
  productName: string;
  segment?: FavoriteProductSegment;
}

type Variables = { data: FavoriteProductData };

interface MutateContext {
  previousFavoriteProducts: CustomerFavoriteProducts | undefined;
}

function usePostFavoriteProductMutation(
  onSuccess?: (variables: Variables) => void
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ data }: Variables) => {
      const res = await postFavoriteProducts({
        customerId: data.customerId,
        body: {
          product_id: data.productId,
        },
      });
      if (res.status !== StatusCode.SuccessNoContent) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }
      return res.data;
    },
    onMutate: async ({ data }: Variables): Promise<MutateContext> => {
      const { customerId, productId: newProductId } = data;

      await queryClient.cancelQueries({
        queryKey: [getFavoriteProductsPath({ customerId })],
      });

      const previousFavoriteProducts: CustomerFavoriteProducts | undefined =
        queryClient.getQueryData([getFavoriteProductsPath({ customerId })]);

      const nextFavoriteProducts = [
        ...(previousFavoriteProducts?.favorite_products || []),
        { product_id: newProductId },
      ];
      queryClient.setQueryData([getFavoriteProductsPath({ customerId })], {
        favorite_products: nextFavoriteProducts,
      });

      return { previousFavoriteProducts };
    },
    onError: (_err, { data }: Variables, context?: MutateContext) => {
      const { customerId } = data;

      toast(
        <Toast variant={ToastVariant.Critical}>
          <Warning />
          Unable to complete action. Please try again.
        </Toast>
      );

      queryClient.setQueryData(
        [getFavoriteProductsPath({ customerId })],
        context?.previousFavoriteProducts
      );
    },
    onSettled: (_data, _err, { data }: Variables) => {
      const filterParam = 'is_favorite=1';

      queryClient.invalidateQueries({
        queryKey: [getFavoriteProductsPath({ customerId: data.customerId })],
      });

      // Keep the owner-view favorites cookbook in sync with the re-favorite.
      queryClient.invalidateQueries({
        queryKey: getFavoritesCookbookQueryKey(),
      });

      // Invalidate product filter queries that include favorites
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'ProductsV2' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].includes(filterParam),
      });
    },
    onSuccess: onSuccess
      ? (_data, variables) => onSuccess(variables)
      : undefined,
  });
}

/**
 * Adds a product to favorites. Fires the default `Product Interaction` (saved)
 * Segment event via `trackFavorite`, but only when the mutation's `data.segment`
 * is provided; if `segment` is omitted, no event is sent. Use
 * `usePostFavoriteProductWithoutTracking` on surfaces (e.g. the Exp 242 home
 * feed) that emit their own interaction event.
 */
export function usePostFavoriteProduct() {
  return usePostFavoriteProductMutation(({ data }) => {
    if (data.segment === undefined) return;

    // Spread rather than enumerate: `segment` is `FeedInteractionFields` plus
    // the favorite-specific keys, and `trackFavorite` accepts exactly that
    // shape, so new spec columns need no edit here.
    trackFavorite({
      ...data.segment,
      id: data.productId,
      name: data.productName,
      category: 'products',
      state: true,
    });
  });
}

/**
 * Adds a product to favorites without firing the default `Product Interaction`
 * event. For surfaces (e.g. the Exp 242 home feed) that emit their own
 * interaction analytics.
 */
export function usePostFavoriteProductWithoutTracking() {
  return usePostFavoriteProductMutation();
}
